import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DataGrid } from '../../Common';
import { NoData } from '../../Common/DataGrid/utils';
import NoDataTableIcon from '../../../Icons/empty_table_placeholder.svg';
import Routes from '../../../routes';
import { columnsForKind } from './surfaceColumns';
import BandControl from '../BandControl';
import { toneFor } from '../severityTokens';

const EXPORT_TYPES = [{ type: 'csv', name: 'CSV' }, { type: 'xlsx', name: 'Excel' }];

// Band chips take the same severity tones as this table's status chips
const bandColors = (kind) => Object.fromEntries(['Hit', 'Near', 'Low'].map((band) => {
    const tone = toneFor(band.toLowerCase(), kind);
    return [band, { bg: tone.background, fg: tone.text }];
}));

const plural = (n, one, many) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

// AC11 state A vs C; C names the control responsible and the count it hides, and never implies compliance
export const emptyStateCopy = ({ tileName, deviceBased, filterCount, searchTerm, hiddenCount }) => {
    const noun = deviceBased ? 'devices' : 'assets';
    // An unknown count (still loading or failed) names the tile without a number rather than guessing (N3)
    const count = hiddenCount === null || hiddenCount === undefined ? tileName : `${hiddenCount.toLocaleString()} ${tileName}`;
    // Nothing hidden behind the controls (tile is 0): state A, never "see all 0"
    if (hiddenCount === 0) {
        return [`No ${tileName}`, `There are currently no ${noun} that meet the criteria for this table.`];
    }
    if (filterCount > 0 && searchTerm) {
        return [`No ${noun} match your search and filters`, `No results for "${searchTerm}" with ${plural(filterCount, 'filter', 'filters')} applied. Clear them to see all ${count}.`];
    }
    if (searchTerm) {
        return [`No ${noun} match your search`, `No results for "${searchTerm}". Clear the search to see all ${count}.`];
    }
    if (filterCount > 0) {
        return [`No ${noun} match your filters`, `${plural(filterCount, 'filter is', 'filters are')} applied. Clear them to see all ${count}.`];
    }
    return [`No ${tileName}`, `There are currently no ${noun} that meet the criteria for this table.`];
};

// AC13: debounced, scoped to this table, never persisted; matches only the visible text columns (server-side)
const SearchBox = ({ value, onChange }) => (
    <TextField
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search"
        inputProps={{ 'aria-label': 'Search this table' }}
        sx={{ width: { xs: '150px', sm: '220px' }, mr: 1, '& .MuiInputBase-root': { fontSize: '13px', height: '32px' } }}
        InputProps={{
            startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
            endAdornment: value ? (
                <InputAdornment position="end">
                    <IconButton size="small" aria-label="Clear search" onClick={() => onChange('')}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </InputAdornment>
            ) : null,
        }}
    />
);

const SurfaceTable = ({ regulation, surfaceKey, surface, copy, valueOptions, pageFilters, unfilteredCount, bands, onBandsChange }) => {
    // Keyed by regulation + surface in the parent, so these are fixed for this mount
    const columns = useMemo(() => columnsForKind(surface.kind, { descriptions: copy?.columnDescriptions, valueOptions }), [surface.kind]);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [visibleFields, setVisibleFields] = useState([]);

    useEffect(() => {
        const timer = setTimeout(() => setSearchTerm(searchInput.trim()), 350);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const hasBands = surface.kind !== 'inScope';
    const bandOptions = surface.supportsLow ? ['Low', 'Near', 'Hit'] : ['Near', 'Hit'];
    const bandValue = bands.join(',');
    // Low only widens the view; turning Hit or Near off narrows it, which counts as one table filter
    const bandsNarrowed = hasBands && !(bands.includes('Hit') && bands.includes('Near'));

    const staticFilters = useMemo(() => [
        ...pageFilters,
        ...(hasBands ? [{ property: 'bands', value: bandValue }] : []),
        ...(searchTerm ? [
            { property: 'search', value: searchTerm },
            { property: 'searchFields', value: visibleFields.join(',') },
        ] : []),
    ], [pageFilters, hasBands, bandValue, searchTerm, visibleFields]);

    const renderNoRows = ({ tableRequest, response }) => {
        const tableFilterCount = (tableRequest?.filters?.length || 0) + (bandsNarrowed ? 1 : 0);
        // AC11: a page-level filter that empties the tile is still a filter, so name what it hides instead of claiming nothing qualifies
        const pageFilterCount = new Set(pageFilters.map((f) => f.property)).size;
        // Only a known 0 without the page filter means nothing qualifies; unknown still reads as filtered (N3)
        const hiddenByPageFilter = response?.tileCount === 0 && pageFilterCount > 0 && unfilteredCount !== 0;
        const [headline, subLine] = emptyStateCopy({
            tileName: surface.name,
            deviceBased: surface.deviceBased,
            filterCount: tableFilterCount + (hiddenByPageFilter ? pageFilterCount : 0),
            searchTerm,
            hiddenCount: hiddenByPageFilter ? (unfilteredCount ?? null) : response?.tileCount,
        });
        return <NoData noTableDataIcon={NoDataTableIcon} noResultText={headline} noResultText2={subLine} />;
    };

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h5" sx={{ fontSize: '1.125rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                {surface.name}
                <Tooltip title={surface.citation}>
                    <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} aria-label={`Citation: ${surface.citation}`} titleAccess={`Citation: ${surface.citation}`} tabIndex={0} />
                </Tooltip>
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 1 }}>
                {copy?.tableLine}
            </Typography>
            <DataGrid
                tableId={`compliance-${regulation.key}-${surfaceKey}`}
                url={Routes.ComplianceCenterRegulation.SurfaceTable(regulation.key, surfaceKey)}
                exportButtonURL={Routes.ComplianceCenterRegulation.SurfaceExport(regulation.key, surfaceKey)}
                exportButtonAlt="Export"
                exportFileTypes={EXPORT_TYPES}
                exportFileName={`${regulation.name} - ${surface.name}`}
                exportWithSort
                columns={columns}
                getRowId={(row) => row.id}
                staticFilters={staticFilters}
                resetPageOnStaticFiltersChange
                onVisibleColumnsChange={setVisibleFields}
                toolbarLeftContent={hasBands ? (
                    <BandControl options={bandOptions} bands={bands} onChange={onBandsChange} colors={bandColors(surface.kind)} />
                ) : null}
                toolbarRightContent={<SearchBox value={searchInput} onChange={setSearchInput} />}
                renderNoRows={renderNoRows}
            />
        </Box>
    );
};

export default SurfaceTable;
